/**
 * ISPCircuits — /wan/circuits
 * All circuits across operators.
 * Expanding a row shows its demarc points with inline add/edit/delete.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const TYPE_LABEL = {
  fiber: 'Fibra', xdsl: 'xDSL', ethernet_l2: 'Eth L2', dia: 'DIA',
  mpls: 'MPLS', lte: 'LTE', satellite: 'SAT', other: 'Otro',
};

const STATUS_BADGE = {
  active:      'text-bg-success',
  planned:     'text-bg-warning text-dark',
  maintenance: 'text-bg-warning text-dark',
  retired:     'text-bg-secondary',
};

const DEMARC_TYPE_LABEL = {
  fdt: 'Caja fibra (FDT)', cto: 'CTO', splitter: 'Splitter', ont: 'ONT', nid: 'NID',
  patch_panel_port: 'Patch Panel', switch_port: 'Puerto switch',
  cpe: 'CPE', other: 'Otro',
};

/** Icono de cada eslabon al pintar la cadena fisica. */
const DEMARC_ICON = {
  fdt: '📦', cto: '📦', splitter: '🔀', ont: '🛰️', nid: '🔌',
  patch_panel_port: '🧩', switch_port: '🔀', cpe: '📡', other: '❓',
};

/**
 * IDs que no pueden ser "eslabon previo" de un punto: el propio punto y todo lo
 * que cuelga de el. Lo mismo que valida el backend, pero aqui para no ofrecer
 * opciones que daran error.
 */
const selfAndDescendants = (demarcId, demarcs) => {
  const blocked = new Set();
  if (!demarcId) return blocked;
  blocked.add(String(demarcId));
  const linked = demarcs.filter(d => d.upstream_demarc_id != null);
  let grew = true;
  while (grew) {
    grew = false;
    for (const d of linked) {
      const parent = String(d.upstream_demarc_id);
      const id = String(d.id);
      if (blocked.has(parent) && !blocked.has(id)) {
        blocked.add(id);
        grew = true;
      }
    }
  }
  return blocked;
};

/**
 * Entrega del circuito en un puerto de un elemento del rack.
 *
 * En CPD el operador entrega muchas veces en un puerto de switch (y de ese
 * switch salen los firewalls), no directamente en el firewall; el patch panel
 * es el otro caso clásico. Ambos se modelan igual: rack → elemento → puerto.
 */
const DEMARC_CHILD = {
  patch_panel_port: {
    element_type: 'panel',
    device_label: 'Patch panel (del rack)',
    device_placeholder: '— Seleccionar patch panel —',
    port_label: 'Puerto del patch panel',
    empty_label: 'Sin patch panels en este rack',
  },
  switch_port: {
    element_type: 'switch',
    device_label: 'Switch (del rack)',
    device_placeholder: '— Seleccionar switch —',
    port_label: 'Puerto del switch',
    empty_label: 'Sin switches en este rack',
  },
};

/** Etiqueta legible de un puerto (patch panel o switch). */
const portLabel = (p) => {
  const raw = (p.label || '').trim();
  if (raw && !/^port\s*\d+$/i.test(raw)) return raw;
  return `Puerto ${p.port_number}`;
};

/** Detalle del puerto para el desplegable: VLAN, velocidad, a dónde va. */
const portDetail = (p) => {
  const bits = [];
  if (p.vlan) bits.push(`VLAN ${p.vlan}`);
  if (p.speed) bits.push(p.speed);
  if (p.poe_enabled) bits.push('PoE');
  if (p.is_patched) bits.push('con latiguillo');
  if (p.is_active === false) bits.push('inactivo');
  if (p.connected_to) bits.push(`→ ${p.connected_to}`);
  return bits.join(' · ');
};

const EMPTY_CIRCUIT = {
  operator_id: '', circuit_id_provider: '', label: '', circuit_type: 'fiber',
  bandwidth_down_mbps: '', bandwidth_up_mbps: '', encapsulation_vlan: '',
  status: 'active', activation_date: '', billing_ref: '', notes: '',
};

export default function ISPCircuits({ companyId }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [circuits, setCircuits]       = useState([]);
  const [operators, setOperators]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const errTimer                      = useRef(null);

  const [search, setSearch]           = useState('');
  const [filterOp, setFilterOp]       = useState(searchParams.get('operator') || '');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType]   = useState('');

  const [formOpen, setFormOpen]       = useState(false);
  const [editId, setEditId]           = useState(null);
  const [form, setForm]               = useState(EMPTY_CIRCUIT);
  const [saving, setSaving]           = useState(false);

  const [expandedId, setExpandedId]   = useState(
    searchParams.get('circuit') ? Number(searchParams.get('circuit')) : null
  );
  const [demarcData, setDemarcData]   = useState({});   // { circuitId: [demarc...] }
  const [loadingDemarc, setLoadingDemarc] = useState(false);

  useEffect(() => {
    if (error) {
      clearTimeout(errTimer.current);
      errTimer.current = setTimeout(() => setError(''), 6000);
    }
    return () => clearTimeout(errTimer.current);
  }, [error]);

  /* Load operators for filter select */
  useEffect(() => {
    if (!companyId) return;
    api.getIspOperators(companyId).then(r => setOperators(r.data || [])).catch(() => {});
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.getIspCircuits(companyId, {
        ...(filterOp     && { operator_id: filterOp }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterType   && { circuit_type: filterType }),
        ...(search       && { q: search }),
      });
      setCircuits(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, filterOp, filterStatus, filterType, search]);

  useEffect(() => { load(); }, [load]);

  /* Expand / collapse demarc */
  const toggleExpand = useCallback((circuitId) => {
    setExpandedId(prev => (prev === circuitId ? null : circuitId));
  }, []);

  /* Carga de demarcaciones del circuito abierto.
     Va en un efecto (y no dentro del clic) para que el enlace directo
     `/wan/circuits?circuit=<id>` cargue la cadena en vez de dejar la fila
     abierta y vacía. */
  useEffect(() => {
    if (!expandedId || !companyId || demarcData[expandedId]) return;
    let cancelled = false;
    setLoadingDemarc(true);
    api.getIspDemarcPoints(expandedId, companyId)
      .then(res => {
        if (!cancelled) {
          setDemarcData(prev => ({ ...prev, [expandedId]: res.data || [] }));
        }
      })
      .catch(() => { /* la fila queda vacía; no se rompe la vista */ })
      .finally(() => { if (!cancelled) setLoadingDemarc(false); });
    return () => { cancelled = true; };
  }, [expandedId, companyId, demarcData]);

  const reloadDemarc = (circuitId) => {
    setDemarcData(prev => { const n = { ...prev }; delete n[circuitId]; return n; });
    // Re-fetch
    api.getIspDemarcPoints(circuitId, companyId)
      .then(res => setDemarcData(prev => ({ ...prev, [circuitId]: res.data || [] })))
      .catch(() => {});
  };

  /* Form handlers */
  const openCreate = () => {
    setEditId(null); setForm({ ...EMPTY_CIRCUIT, operator_id: filterOp || '' }); setFormOpen(true);
  };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      operator_id: c.operator_id, circuit_id_provider: c.circuit_id_provider,
      label: c.label || '', circuit_type: c.circuit_type || 'fiber',
      bandwidth_down_mbps: c.bandwidth_down_mbps ?? '', bandwidth_up_mbps: c.bandwidth_up_mbps ?? '',
      encapsulation_vlan: c.encapsulation_vlan ?? '', status: c.status || 'active',
      activation_date: c.activation_date || '', billing_ref: c.billing_ref || '', notes: c.notes || '',
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        bandwidth_down_mbps: form.bandwidth_down_mbps ? Number(form.bandwidth_down_mbps) : null,
        bandwidth_up_mbps:   form.bandwidth_up_mbps   ? Number(form.bandwidth_up_mbps)   : null,
        encapsulation_vlan:  form.encapsulation_vlan  ? Number(form.encapsulation_vlan)  : null,
        activation_date:     form.activation_date || null,
      };
      if (editId) {
        await api.updateIspCircuit(editId, payload, companyId);
      } else {
        await api.createIspCircuit(payload, companyId);
      }
      setFormOpen(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar circuito "${c.circuit_id_provider}"?`)) return;
    try {
      await api.deleteIspCircuit(c.id, companyId);
      await load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="container-fluid py-3">
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {/* Toolbar */}
      <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
        <h5 className="mb-0 me-2">🔌 Circuitos WAN</h5>
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 200 }}
          placeholder="Ref. ISP o label…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="form-select form-select-sm" style={{ maxWidth: 160 }}
          value={filterOp} onChange={e => setFilterOp(e.target.value)}>
          <option value="">Todos los operadores</option>
          {operators.map(op => <option key={op.id} value={op.id}>{op.name}</option>)}
        </select>
        <select className="form-select form-select-sm" style={{ maxWidth: 130 }}
          value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="planned">Planificado</option>
          <option value="maintenance">Mantenimiento</option>
          <option value="retired">Retirado</option>
        </select>
        <select className="form-select form-select-sm" style={{ maxWidth: 120 }}
          value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Todos los tipos</option>
          {Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span className="badge text-bg-secondary ms-auto">
          {circuits.length} circuito{circuits.length !== 1 ? 's' : ''}
        </span>
        <button className="btn btn-primary btn-sm" onClick={openCreate}>+ Nuevo circuito</button>
        <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>↺</button>
        <button className="btn btn-outline-info btn-sm" onClick={() => navigate('/wan/operators')}>← Operadores</button>
      </div>

      {/* Create / Edit form */}
      {formOpen && (
        <div className="card mb-3 border-primary shadow-sm">
          <div className="card-header bg-primary bg-opacity-10 d-flex justify-content-between py-2">
            <strong>{editId ? 'Editar circuito' : 'Nuevo circuito'}</strong>
            <button className="btn-close" onClick={() => setFormOpen(false)} />
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit} className="row g-2">
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Operador *</label>
                <select className="form-select form-select-sm" required value={form.operator_id}
                  onChange={e => setForm(f => ({ ...f, operator_id: e.target.value }))}>
                  <option value="">— Seleccionar —</option>
                  {operators.map(op => <option key={op.id} value={op.id}>{op.name}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Referencia ISP *</label>
                <input className="form-control form-control-sm" required value={form.circuit_id_provider}
                  placeholder="ID del circuito en el ISP" style={{ fontFamily: 'monospace' }}
                  onChange={e => setForm(f => ({ ...f, circuit_id_provider: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Label interno</label>
                <input className="form-control form-control-sm" value={form.label}
                  placeholder="ISP-MAD-DIA-1G-0001"
                  onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
              </div>
              <div className="col-md-1">
                <label className="form-label form-label-sm mb-1">Tipo</label>
                <select className="form-select form-select-sm" value={form.circuit_type}
                  onChange={e => setForm(f => ({ ...f, circuit_type: e.target.value }))}>
                  {Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="col-md-1">
                <label className="form-label form-label-sm mb-1">Estado</label>
                <select className="form-select form-select-sm" value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Activo</option>
                  <option value="planned">Planificado</option>
                  <option value="maintenance">Mantenimiento</option>
                  <option value="retired">Retirado</option>
                </select>
              </div>
              <div className="col-md-1">
                <label className="form-label form-label-sm mb-1">↓ Mbps</label>
                <input className="form-control form-control-sm" type="number" value={form.bandwidth_down_mbps}
                  onChange={e => setForm(f => ({ ...f, bandwidth_down_mbps: e.target.value }))} />
              </div>
              <div className="col-md-1">
                <label className="form-label form-label-sm mb-1">↑ Mbps</label>
                <input className="form-control form-control-sm" type="number" value={form.bandwidth_up_mbps}
                  onChange={e => setForm(f => ({ ...f, bandwidth_up_mbps: e.target.value }))} />
              </div>
              <div className="col-md-1">
                <label className="form-label form-label-sm mb-1">VLAN ISP</label>
                <input className="form-control form-control-sm" type="number" value={form.encapsulation_vlan}
                  onChange={e => setForm(f => ({ ...f, encapsulation_vlan: e.target.value }))} />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm mb-1">Activación</label>
                <input className="form-control form-control-sm" type="date" value={form.activation_date}
                  onChange={e => setForm(f => ({ ...f, activation_date: e.target.value }))} />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm mb-1">Ref. facturación</label>
                <input className="form-control form-control-sm" value={form.billing_ref}
                  onChange={e => setForm(f => ({ ...f, billing_ref: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label form-label-sm mb-1">Notas</label>
                <input className="form-control form-control-sm" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="col-12 d-flex justify-content-end gap-2 mt-1">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setFormOpen(false)}>Cancelar</button>
                <button type="submit" className="btn btn-sm btn-success" disabled={saving}>
                  {saving ? 'Guardando…' : editId ? 'Guardar cambios' : 'Crear circuito'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Circuit list */}
      {loading && <p className="text-muted text-center py-4">Cargando circuitos…</p>}
      {!loading && circuits.length === 0 && (
        <p className="text-muted text-center py-4">Sin circuitos. Crea el primero o ajusta los filtros.</p>
      )}

      <div className="d-grid gap-2">
        {circuits.map(c => {
          const isExpanded = expandedId === c.id;
          const demarcs    = demarcData[c.id];
          const bw = c.bandwidth_down_mbps
            ? `${c.bandwidth_down_mbps}↓ / ${c.bandwidth_up_mbps || '?'}↑ Mbps`
            : null;

          return (
            <div key={c.id} className="card shadow-sm">
              {/* Circuit row */}
              <div
                className="card-body py-2 px-3 d-flex align-items-center gap-3"
                role="button"
                onClick={() => toggleExpand(c.id)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ minWidth: 200 }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.92em' }}>
                    {c.circuit_id_provider}
                  </span>
                  {c.label && (
                    <span className="text-muted ms-2" style={{ fontSize: '0.8em' }}>{c.label}</span>
                  )}
                </div>

                <div className="d-flex gap-1 flex-wrap">
                  <span className={`badge ${STATUS_BADGE[c.status] || 'text-bg-secondary'}`} style={{ fontSize: '0.7em' }}>
                    {c.status}
                  </span>
                  <span className="badge text-bg-light border text-dark" style={{ fontSize: '0.7em' }}>
                    {TYPE_LABEL[c.circuit_type] || c.circuit_type}
                  </span>
                  {bw && (
                    <span className="badge text-bg-light border text-dark" style={{ fontSize: '0.7em' }}>{bw}</span>
                  )}
                  {c.encapsulation_vlan && (
                    <span className="badge text-bg-light border text-dark" style={{ fontSize: '0.7em', fontFamily: 'monospace' }}>
                      VLAN {c.encapsulation_vlan}
                    </span>
                  )}
                  <span className="badge text-bg-info text-dark" style={{ fontSize: '0.7em' }}>
                    {c.demarc_count || 0} demarc
                  </span>
                </div>

                <div className="flex-grow-1 text-muted" style={{ fontSize: '0.8em' }}>
                  {c.operator_name && <span>📡 {c.operator_name}</span>}
                  {c.billing_ref   && <span className="ms-3">💶 {c.billing_ref}</span>}
                  {c.activation_date && <span className="ms-3">📅 {c.activation_date}</span>}
                </div>

                <div className="d-flex gap-1 ms-auto" onClick={e => e.stopPropagation()}>
                  <button className="btn btn-outline-secondary btn-sm py-0 px-2" style={{ fontSize: '0.78em' }}
                    onClick={() => openEdit(c)}>✎</button>
                  <button className="btn btn-outline-danger btn-sm py-0 px-2" style={{ fontSize: '0.78em' }}
                    onClick={() => handleDelete(c)}>✕</button>
                </div>

                <span style={{ fontSize: '0.85em', color: '#6c757d' }}>{isExpanded ? '▲' : '▼'}</span>
              </div>

              {/* Expanded: demarc points */}
              {isExpanded && (
                <div className="border-top bg-light px-3 py-2">
                  {loadingDemarc && !demarcs && (
                    <p className="text-muted py-2 mb-0" style={{ fontSize: '0.85em' }}>Cargando puntos de demarcación…</p>
                  )}
                  {demarcs && <DemarcChain demarcs={demarcs} />}
                  {demarcs && (
                    <DemarcList
                      demarcs={demarcs}
                      circuitId={c.id}
                      companyId={companyId}
                      onReload={() => reloadDemarc(c.id)}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Cadena fisica del circuito (FDT → ONT → router) ─────────────────────── */

/**
 * Agrupa los eslabones en ramas: una lista por cada entrega del operador. Un
 * circuito puede tener varias acometidas (dos cajas de fibra, por ejemplo) y
 * pintarlas todas en una unica linea haria creer que una va tras la otra.
 */
function buildChains(demarcs) {
  const byId = new Map(demarcs.map(d => [d.id, d]));
  const children = new Map();
  demarcs.forEach(d => {
    const parent = d.upstream_demarc_id != null && byId.has(d.upstream_demarc_id)
      ? d.upstream_demarc_id : null;
    if (!children.has(parent)) children.set(parent, []);
    children.get(parent).push(d);
  });

  const chains = [];
  const seen = new Set();
  const walk = (node, acc) => {
    if (seen.has(node.id)) return;
    seen.add(node.id);
    acc.push(node);
    (children.get(node.id) || []).forEach(child => walk(child, acc));
  };

  (children.get(null) || []).forEach(root => {
    const acc = [];
    walk(root, acc);
    if (acc.length) chains.push(acc);
  });
  // Huerfanos (padre fuera del circuito) o ciclos: no se pierde ninguno.
  demarcs.forEach(d => {
    const acc = [];
    walk(d, acc);
    if (acc.length) chains.push(acc);
  });
  return chains;
}

function DemarcChain({ demarcs }) {
  if (!demarcs || demarcs.length < 2) return null;
  const chains = buildChains(demarcs);
  if (!chains.length) return null;

  return (
    <div
      className="d-flex flex-column gap-1 mb-2 px-2 py-1 border rounded"
      style={{ background: 'var(--bg-card)', fontSize: '0.8em' }}
    >
      <span className="text-muted">Cadena física</span>
      {chains.map((branch, bi) => (
        <div key={`chain-${bi}`} className="d-flex align-items-center flex-wrap gap-2">
          {chains.length > 1 && (
            <span className="text-muted" style={{ fontSize: '0.92em' }}>
              Acometida {bi + 1}
            </span>
          )}
          {branch.map((dp, i) => (
            <React.Fragment key={dp.id}>
              {i > 0 && <span className="text-muted">→</span>}
              <span
                className="badge text-bg-light border text-dark"
                title={`${dp.demarc_type_label || dp.demarc_type}${
                  dp.rack_location ? ` · ${dp.rack_location}` : ''
                }${dp.device_interface ? ` · ${dp.device_interface}` : ''}`}
              >
                {DEMARC_ICON[dp.demarc_type] || '❓'}{' '}
                {dp.display_label || dp.demarc_type_label}
              </span>
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── Demarc list inside an expanded circuit row ───────────────────────────── */
const EMPTY_DEMARC_F = {
  demarc_type: 'ont', physical_id: '', rack_location: '',
  site_id: '', device_type: '', device_id: '', device_interface: '', notes: '',
  upstream_demarc_id: '',
  // transient UI helpers (stripped before submit)
  _rackId: '', _childId: '',
};

function DemarcList({ demarcs, circuitId, companyId, onReload }) {
  const [addOpen,    setAddOpen]    = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [form,       setForm]       = useState(EMPTY_DEMARC_F);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');

  /* ── CMDB catalogs ──────────────────────────────────────────────────────── */
  const [locations,    setLocations]    = useState([]);
  const [firewalls,    setFirewalls]    = useState([]);
  const [servers,      setServers]      = useState([]);
  const [routers,      setRouters]      = useState([]);
  const [switches,     setSwitches]     = useState([]);
  const [racks,        setRacks]        = useState([]);
  /* Elemento del rack que entrega el circuito (patch panel o switch) y sus puertos */
  const [childDevices, setChildDevices] = useState([]);
  const [childPorts,   setChildPorts]   = useState([]);
  const [fwInterfaces, setFwInterfaces] = useState([]);
  const [switchPorts,  setSwitchPorts]  = useState([]);
  const [catalogsLoaded, setCatalogsLoaded] = useState(false);

  /* Rack → elemento que entrega (patch panel o switch) */
  const childSpec = DEMARC_CHILD[form.demarc_type] || null;

  /* Load all catalogs once when this row is expanded */
  useEffect(() => {
    if (!companyId || catalogsLoaded) return;
    Promise.all([
      api.getLocations('', companyId),
      api.getFirewalls(companyId),
      api.getServers(companyId),
      api.getRouters(companyId),
      api.getElements(companyId, { element_type: 'switch' }),
      api.getRacks(null, companyId),
    ]).then(([locR, fwR, srvR, rtrR, swR, rackR]) => {
      setLocations(locR.data || []);
      setFirewalls(fwR.data || []);
      setServers(srvR.data || []);
      setRouters(rtrR.data || []);
      setSwitches(swR.data || []);
      setRacks(rackR.data || []);
      setCatalogsLoaded(true);
    }).catch(() => { setCatalogsLoaded(true); });
  }, [companyId, catalogsLoaded]);

  /* Load firewall interfaces when FW device is selected */
  useEffect(() => {
    if (form.device_type === 'firewall' && form.device_id) {
      api.getFirewallInterfaces(form.device_id, companyId)
        .then(r => setFwInterfaces(r.data || []))
        .catch(() => setFwInterfaces([]));
    } else {
      setFwInterfaces([]);
    }
  }, [form.device_type, form.device_id, companyId]);

  /* Load switch ports when the internal device is a switch */
  useEffect(() => {
    if (form.device_type === 'switch' && form.device_id) {
      api.getPorts(form.device_id, companyId)
        .then(r => setSwitchPorts(r.data || []))
        .catch(() => setSwitchPorts([]));
    } else {
      setSwitchPorts([]);
    }
  }, [form.device_type, form.device_id, companyId]);

  /* Elementos del rack que pueden entregar el circuito (patch panels o switches) */
  useEffect(() => {
    if (childSpec && form._rackId) {
      api.getElements(companyId, { element_type: childSpec.element_type, rack_id: form._rackId })
        .then(r => setChildDevices(r.data || []))
        .catch(() => setChildDevices([]));
    } else {
      setChildDevices([]);
      setChildPorts([]);
    }
  }, [childSpec, form._rackId, companyId]);

  /* Puertos del elemento seleccionado (patch panel o switch) */
  useEffect(() => {
    if (form._childId) {
      api.getPorts(form._childId, companyId)
        .then(r => setChildPorts(r.data || []))
        .catch(() => setChildPorts([]));
    } else {
      setChildPorts([]);
    }
  }, [form._childId, companyId]);

  /* ── Helpers ─────────────────────────────────────────────────────────────── */
  const deviceName = (dp) => {
    if (!dp.device_type || !dp.device_id) return '—';
    if (dp.device_type === 'firewall') {
      const fw = firewalls.find(f => f.id === dp.device_id);
      return fw ? `🔥 ${fw.name}${fw.hostname ? ` (${fw.hostname})` : ''}` : `FW #${dp.device_id}`;
    }
    if (dp.device_type === 'server') {
      const sv = servers.find(s => s.id === dp.device_id);
      const hn = sv?.config?.hostname || sv?.hostname;
      return sv ? `🖥 ${sv.name || hn || `Srv#${dp.device_id}`}` : `Srv #${dp.device_id}`;
    }
    if (dp.device_type === 'switch') {
      const sw = switches.find(s => s.id === dp.device_id);
      return sw ? `🔀 ${sw.name}` : `Switch #${dp.device_id}`;
    }
    // El CPE del operador se registra en la CMDB como router (ver el sintético)
    if (dp.device_type === 'cpe' || dp.device_type === 'router') {
      const rt = routers.find(r => r.id === dp.device_id);
      return rt ? `📡 ${rt.name}` : `${dp.device_type.toUpperCase()} #${dp.device_id}`;
    }
    return `${dp.device_type}${dp.device_id ? ` #${dp.device_id}` : ''}`;
  };

  const locationName = (dp) => {
    if (!dp.site_id) return '—';
    const loc = locations.find(l => l.id === dp.site_id);
    return loc ? `📍 ${loc.name}` : `Site #${dp.site_id}`;
  };

  /* ── Form open/close ─────────────────────────────────────────────────────── */
  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_DEMARC_F);
    setAddOpen(true);
  };

  const openEdit = (dp) => {
    setEditingId(dp.id);
    setForm({
      demarc_type:      dp.demarc_type         || 'ont',
      physical_id:      dp.physical_id         || '',
      rack_location:    dp.rack_location        || '',
      site_id:          dp.site_id              || '',
      device_type:      dp.device_type          || '',
      device_id:        dp.device_id            || '',
      device_interface: dp.device_interface     || '',
      upstream_demarc_id: dp.upstream_demarc_id != null ? String(dp.upstream_demarc_id) : '',
      notes:            dp.notes               || '',
      _rackId:          '',
      _childId:         '',
    });
    setAddOpen(true);
  };

  /* IDs vetados como "va detrás de": uno mismo y sus descendientes (sin ciclos) */
  const blockedUpstreamIds = selfAndDescendants(editingId, demarcs);
  const upstreamOptions = demarcs.filter(dp => !blockedUpstreamIds.has(String(dp.id)));

  /* Interfaces del router elegido como equipo interno (ya vienen en el catálogo) */
  const selectedRouter = form.device_type === 'router'
    ? routers.find(r => String(r.id) === String(form.device_id))
    : null;
  const routerIfaces = selectedRouter?.interfaces || [];

  /* ── Submit ──────────────────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Strip transient UI helpers before sending
      const { _rackId, _childId, ...payload } = form;
      const clean = {
        ...payload,
        site_id:   payload.site_id   ? Number(payload.site_id)   : null,
        device_id: payload.device_id ? Number(payload.device_id) : null,
        upstream_demarc_id: payload.upstream_demarc_id
          ? Number(payload.upstream_demarc_id) : null,
      };
      if (editingId) {
        await api.updateIspDemarcPoint(editingId, clean, companyId);
      } else {
        await api.createIspDemarcPoint(circuitId, clean, companyId);
      }
      setAddOpen(false);
      onReload();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const handleDelete = async (dp) => {
    if (!window.confirm('¿Eliminar punto de demarcación?')) return;
    try {
      await api.deleteIspDemarcPoint(dp.id, companyId);
      onReload();
    } catch (e) { setError(e.message); }
  };

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-1">
        <strong style={{ fontSize: '0.82em' }}>Puntos de demarcación</strong>
      </div>
      {error && (
        <div className="alert alert-danger py-1 px-2 mb-1 d-flex justify-content-between" style={{ fontSize: '0.8em' }}>
          <span>{error}</span>
          <button className="btn-close btn-sm" onClick={() => setError('')} style={{ fontSize: '0.7em' }} />
        </div>
      )}

      {demarcs.length === 0 && !addOpen && (
        <p className="text-muted mb-1" style={{ fontSize: '0.82em' }}>Sin puntos de demarcación registrados.</p>
      )}

      {demarcs.length > 0 && (
        <table className="table table-sm table-borderless mb-1" style={{ fontSize: '0.8em' }}>
          <thead>
            <tr className="text-muted">
              <th style={{ width: 26 }}>#</th>
              <th>Tipo</th><th>Ubicación</th><th>ID físico / Rack</th>
              <th>Equipo CMDB</th><th>Interfaz</th><th>Notas</th><th></th>
            </tr>
          </thead>
          <tbody>
            {demarcs.map((dp, idx) => (
              <tr key={dp.id}>
                <td className="text-muted">{idx + 1}</td>
                <td><span className="badge text-bg-light border text-dark">{DEMARC_TYPE_LABEL[dp.demarc_type] || dp.demarc_type}</span></td>
                <td style={{ fontSize: '0.85em' }}>{locationName(dp)}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                  {dp.physical_id && <div>{dp.physical_id}</div>}
                  {dp.rack_location && <div className="text-muted">{dp.rack_location}</div>}
                  {!dp.physical_id && !dp.rack_location && '—'}
                </td>
                <td style={{ fontSize: '0.85em' }}>{deviceName(dp)}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{dp.device_interface || '—'}</td>
                <td className="text-muted" style={{ fontSize: '0.85em' }}>{dp.notes || '—'}</td>
                <td className="d-flex gap-1">
                  <button className="btn btn-outline-secondary btn-sm py-0 px-1"
                    style={{ fontSize: '0.7em' }} onClick={() => openEdit(dp)}>✎</button>
                  <button className="btn btn-outline-danger btn-sm py-0 px-1"
                    style={{ fontSize: '0.7em' }} onClick={() => handleDelete(dp)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Add / Edit form ── */}
      {!addOpen ? (
        <button className="btn btn-outline-primary btn-sm" style={{ fontSize: '0.78em' }} onClick={openAdd}>
          + Añadir demarc
        </button>
      ) : (
        /* El fondo del formulario tiene que salir del tema (`--bg-card` vía .card),
           no de un `bg-white` fijo: con un skin oscuro el bloque blanco dejaba los
           labels (texto claro) y los selects (fondo `--bg-input` claro) ilegibles. */
        <form onSubmit={handleSubmit} className="card p-3 shadow-sm mt-2">
          <div className="row g-2 mb-2">
            {/* Row 1: type, site (location), rack, patch panel / switch */}
            <div className="col-md-2">
              <label className="form-label form-label-sm mb-1">Tipo demarc</label>
              <select className="form-select form-select-sm" value={form.demarc_type}
                onChange={e => setForm(f => ({ ...f, demarc_type: e.target.value, _childId: '', physical_id: '' }))}>
                {Object.entries(DEMARC_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-sm mb-1">Sede / ubicación</label>
              <select className="form-select form-select-sm" value={form.site_id}
                onChange={e => setForm(f => ({ ...f, site_id: e.target.value }))}>
                <option value="">— Sin sede —</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}{l.city ? ` · ${l.city}` : ''}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-sm mb-1">
                Rack{childSpec ? ' *' : ' (opcional)'}
              </label>
              <select className="form-select form-select-sm" value={form._rackId}
                onChange={e => {
                  const rack = racks.find(r => String(r.id) === e.target.value);
                  setForm(f => ({
                    ...f,
                    _rackId: e.target.value,
                    _childId: '',
                    rack_location: rack ? rack.name : f.rack_location,
                  }));
                }}>
                <option value="">— Seleccionar rack —</option>
                {racks.map(r => (
                  <option key={r.id} value={r.id}>{r.name}{r.location_name ? ` · ${r.location_name}` : ''}</option>
                ))}
              </select>
            </div>

            {/* Patch panel / switch of the rack — only for the port-based demarc types */}
            {childSpec && (
              <div className="col-md-4">
                <label className="form-label form-label-sm mb-1">{childSpec.device_label}</label>
                <select className="form-select form-select-sm" value={form._childId}
                  onChange={e => {
                    const dev = childDevices.find(d => String(d.id) === e.target.value);
                    setForm(f => ({
                      ...f,
                      _childId: e.target.value,
                      physical_id: dev ? `${dev.name} · Puerto ` : f.physical_id,
                      // Si la entrega es un switch y aún no se ha elegido equipo interno,
                      // se enlaza el propio switch como CI (es el que recibe el circuito).
                      ...(dev && childSpec.element_type === 'switch' && !f.device_type
                        ? { device_type: 'switch', device_id: String(dev.id) }
                        : {}),
                    }));
                  }}>
                  <option value="">{childSpec.device_placeholder}</option>
                  {childDevices.length === 0 && form._rackId && (
                    <option disabled>{childSpec.empty_label}</option>
                  )}
                  {childDevices.map(d => (
                    <option key={d.id} value={d.id}>{d.name}{d.position_u ? ` (U${d.position_u})` : ''}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="row g-2 mb-2">
            {/* Row 2: physical_id (port select or text), rack_location text, notes */}
            <div className="col-md-4">
              {childSpec && form._childId ? (
                <>
                  <label className="form-label form-label-sm mb-1">{childSpec.port_label}</label>
                  <select className="form-select form-select-sm" value={form.physical_id}
                    onChange={e => {
                      const port = childPorts.find(p => {
                        const dev = childDevices.find(d => String(d.id) === String(form._childId));
                        return `${dev ? dev.name : 'SW'} · ${portLabel(p)}` === e.target.value;
                      });
                      setForm(f => ({
                        ...f,
                        physical_id: e.target.value,
                        // Si es un switch y todavía no hay interfaz, el propio puerto la define.
                        ...(port && childSpec.element_type === 'switch' && !f.device_interface
                          ? { device_interface: portLabel(port) }
                          : {}),
                      }));
                    }}>
                    <option value="">— Seleccionar puerto —</option>
                    {childPorts.length === 0 && (
                      <option disabled>Sin puertos registrados</option>
                    )}
                    {childPorts.map(p => {
                      const dev = childDevices.find(d => String(d.id) === String(form._childId));
                      const val = `${dev ? dev.name : 'SW'} · ${portLabel(p)}`;
                      const detail = portDetail(p);
                      return (
                        <option key={p.id} value={val}>
                          {portLabel(p)}{detail ? ` · ${detail}` : ''}
                        </option>
                      );
                    })}
                  </select>
                  {form.physical_id && (
                    <div className="form-text" style={{ fontSize: '0.72em' }}>Se guardará como: <code>{form.physical_id}</code></div>
                  )}
                </>
              ) : (
                <>
                  <label className="form-label form-label-sm mb-1">
                    {childSpec ? 'ID físico (elige rack y ' + (childSpec.element_type === 'switch' ? 'switch' : 'panel') + ' primero)' : 'ID físico (serial / referencia ISP)'}
                  </label>
                  <input className="form-control form-control-sm" style={{ fontFamily: 'monospace' }}
                    placeholder={childSpec ? (childSpec.element_type === 'switch' ? 'SW-A · Gi1/0/3' : 'PP-1 · Port 12') : 'ONT-XYZ123 / SN-ABCDEF'}
                    value={form.physical_id}
                    onChange={e => setForm(f => ({ ...f, physical_id: e.target.value }))} />
                </>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label form-label-sm mb-1">Ubicación en rack (texto libre)</label>
              <input className="form-control form-control-sm" style={{ fontFamily: 'monospace' }}
                placeholder="DC1 · Rack-07 · U14"
                value={form.rack_location}
                onChange={e => setForm(f => ({ ...f, rack_location: e.target.value }))} />
              <div className="form-text" style={{ fontSize: '0.72em' }}>Rellenado automáticamente al seleccionar el rack</div>
            </div>

            <div className="col-md-4">
              <label className="form-label form-label-sm mb-1">Notas</label>
              <input className="form-control form-control-sm" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>

          <div className="row g-2">
            {/* Row 3: device type, device picker, interface picker */}
            <div className="col-md-2">
              <label className="form-label form-label-sm mb-1">Tipo equipo interno</label>
              <select className="form-select form-select-sm" value={form.device_type}
                onChange={e => setForm(f => ({ ...f, device_type: e.target.value, device_id: '', device_interface: '' }))}>
                <option value="">— Ninguno —</option>
                <option value="firewall">Firewall</option>
                <option value="router">Router</option>
                <option value="server">Servidor</option>
                <option value="switch">Switch</option>
                <option value="cpe">CPE operador</option>
                <option value="other">Otro</option>
              </select>
            </div>

            {/* Eslabón previo: por dónde viene la fibra antes de este punto.
                Vacío = es la entrega del operador (raíz de la cadena). */}
            <div className="col-md-3">
              <label className="form-label form-label-sm mb-1">Va detrás de</label>
              <select className="form-select form-select-sm" value={form.upstream_demarc_id}
                onChange={e => setForm(f => ({ ...f, upstream_demarc_id: e.target.value }))}>
                <option value="">— Nada: es la entrega del operador —</option>
                {upstreamOptions.map(dp => (
                  <option key={dp.id} value={dp.id}>
                    {(DEMARC_ICON[dp.demarc_type] || '❓')}{' '}
                    {dp.display_label || dp.demarc_type_label || dp.demarc_type}
                    {dp.device_type === 'router' || dp.device_type === 'cpe' ? ' (router)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Device picker — switch (entrega en un switch de tránsito) */}
            {form.device_type === 'switch' && (
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Switch (CMDB)</label>
                <select className="form-select form-select-sm" value={form.device_id}
                  onChange={e => setForm(f => ({ ...f, device_id: e.target.value, device_interface: '' }))}>
                  <option value="">— Seleccionar switch —</option>
                  {switches.map(sw => {
                    const rack = racks.find(r => r.id === sw.rack_id);
                    return (
                      <option key={sw.id} value={sw.id}>
                        {sw.name}{rack ? ` · ${rack.name}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Switch ports — el puerto por el que entra/entrega el circuito.
                Si el switch es el mismo que ya se eligió arriba en la entrega de la
                demarcación, el puerto ya está marcado: no se pide otra vez. */}
            {form.device_type === 'switch' && form.device_id && switchPorts.length > 0 &&
              !(childSpec?.element_type === 'switch' && String(form._childId) === String(form.device_id)) && (
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Puerto del switch</label>
                <select className="form-select form-select-sm" value={form.device_interface}
                  onChange={e => setForm(f => ({ ...f, device_interface: e.target.value }))}>
                  <option value="">— Seleccionar puerto —</option>
                  {switchPorts.map(p => {
                    const name = portLabel(p);
                    const detail = portDetail(p);
                    return (
                      <option key={p.id} value={name}>
                        {name}{detail ? ` · ${detail}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Device picker — firewall */}
            {form.device_type === 'firewall' && (
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Firewall (CMDB)</label>
                <select className="form-select form-select-sm" value={form.device_id}
                  onChange={e => setForm(f => ({ ...f, device_id: e.target.value, device_interface: '' }))}>
                  <option value="">— Seleccionar firewall —</option>
                  {firewalls.map(fw => (
                    <option key={fw.id} value={fw.id}>
                      {fw.name}{fw.hostname ? ` · ${fw.hostname}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Device picker — server */}
            {form.device_type === 'server' && (
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Servidor (CMDB)</label>
                <select className="form-select form-select-sm" value={form.device_id}
                  onChange={e => setForm(f => ({ ...f, device_id: e.target.value, device_interface: '' }))}>
                  <option value="">— Seleccionar servidor —</option>
                  {servers.map(sv => {
                    const hn = sv.config?.hostname || sv.hostname;
                    const label = sv.name || hn || `Servidor #${sv.id}`;
                    return <option key={sv.id} value={sv.id}>{label}{hn && sv.name && hn !== sv.name ? ` · ${hn}` : ''}</option>;
                  })}
                </select>
              </div>
            )}

            {/* Device picker — router (CPE interno / router del cliente) */}
            {form.device_type === 'router' && (
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Router (CMDB)</label>
                <select className="form-select form-select-sm" value={form.device_id}
                  onChange={e => setForm(f => ({ ...f, device_id: e.target.value, device_interface: '' }))}>
                  <option value="">— Seleccionar router —</option>
                  {routers.map(rt => (
                    <option key={rt.id} value={rt.id}>
                      {rt.name}{rt.model ? ` · ${rt.model}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Interface picker — router con interfaces cargadas */}
            {form.device_type === 'router' && form.device_id && routerIfaces.length > 0 && (
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Interfaz del router</label>
                <select className="form-select form-select-sm" value={form.device_interface}
                  onChange={e => setForm(f => ({ ...f, device_interface: e.target.value }))}>
                  <option value="">— Seleccionar interfaz —</option>
                  {routerIfaces.map(iface => (
                    <option key={iface.id} value={iface.name}>
                      {iface.name}{iface.iface_type_label ? ` · ${iface.iface_type_label}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Interface picker — firewall with interfaces loaded */}
            {form.device_type === 'firewall' && form.device_id && fwInterfaces.length > 0 && (
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Interfaz de firewall</label>
                <select className="form-select form-select-sm" value={form.device_interface}
                  onChange={e => setForm(f => ({ ...f, device_interface: e.target.value }))}>
                  <option value="">— Seleccionar interfaz —</option>
                  {fwInterfaces.map(iface => (
                    <option key={iface.id} value={iface.name}>
                      {iface.name}{iface.ifname && iface.ifname !== iface.name ? ` (${iface.ifname})` : ''}
                      {iface.zone ? ` · ${iface.zone}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Interfaz heredada del puerto elegido en la entrega (mismo switch) */}
            {form.device_type === 'switch' && childSpec?.element_type === 'switch' &&
              form._childId && String(form._childId) === String(form.device_id) && form.device_interface && (
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Interfaz (del puerto elegido)</label>
                <input className="form-control form-control-sm" readOnly
                  style={{ fontFamily: 'monospace' }} value={form.device_interface} />
              </div>
            )}

            {/* Interface text — when FW/SW but no interfaces loaded, or for server/other */}
            {(
              (form.device_type === 'firewall' && form.device_id && fwInterfaces.length === 0) ||
              (form.device_type === 'switch' && form.device_id && switchPorts.length === 0) ||
              (form.device_type === 'router' && !(form.device_id && routerIfaces.length > 0)) ||
              form.device_type === 'server' ||
              form.device_type === 'cpe' ||
              form.device_type === 'other'
            ) && (
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Interfaz / Puerto</label>
                <input className="form-control form-control-sm" style={{ fontFamily: 'monospace' }}
                  placeholder="eth0, bond0, Gi1/0/24…"
                  value={form.device_interface}
                  onChange={e => setForm(f => ({ ...f, device_interface: e.target.value }))} />
              </div>
            )}

            <div className="col d-flex align-items-end justify-content-end gap-2">
              <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={() => setAddOpen(false)}>Cancelar</button>
              <button type="submit" className="btn btn-sm btn-success" disabled={saving}>
                {saving ? 'Guardando…' : editingId ? 'Guardar cambios' : '✓ Crear demarc'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

